import axios from 'axios';
import store from '@/store';
import { router } from '@/main';

const baseURL = process.env.VUE_APP_API_URL;
// const getAuthHeader = (token) => ({ Authorization: `Bearer ${token}` });
const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const apiRequest = async (method, url, data = null, params = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const config = {
                method: method,
                url: `${baseURL}/accounts/${store.state.user.user.account_id}/${url}`,
                headers: getAuthHeader(token),
                params: { ...params },
                data: data,
            };
            return await axios(config);
        });
    } catch (error) {
        throw error;
    }
};

const agentSettingsService = {
    getAgentSettings() {
        return apiRequest('get', `agent-settings/${store.state.user.user.account_id}?ts=${new Date().getTime()}`);
    },

    editAgentSettings(updateData) {
        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}`, updateData);
    },

    addSkillToAgentSettings(skillData) {
        console.log('addSkillToAgentSettings', skillData);
        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}/skills`, skillData);
    },

    removeSkillFromAgentSettings(skillId, skillData) {
        return apiRequest('delete', `agent-settings/${store.state.user.user.account_id}/skills/${skillId}`, skillData);
    },

    removeAppAccountSkillFromAgentSettings(skillId, appAccountData) {
        return apiRequest(
            'delete',
            `agent-settings/${store.state.user.user.account_id}/skills/${skillId}/app-accounts`,
            {
                config_schema_values: appAccountData,
            },
        );
    },

    /**
     * Merges keys into one connected account of a multi-account skill (e.g. the name of a
     * Restoo restaurant). Works whether the account lives in config_values or app_accounts.
     * @param {string} skillId
     * @param {Object.<string, string>} match Entries that identify the account, e.g. `{ businessId }`
     * @param {Object.<string, string>} configValues Keys to merge
     * @returns {Promise}
     */
    updateAppAccountSkillFromAgentSettings(skillId, match, configValues) {
        return apiRequest(
            'patch',
            `agent-settings/${store.state.user.user.account_id}/skills/${skillId}/app-accounts`,
            {
                match,
                config_values: configValues,
            },
        );
    },

    addAppAccountSkillFromAgentSettings(skillId, appAccountData) {
        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}/skills/${skillId}/app-accounts`, {
            config_schema_values: appAccountData,
        });
    },

    /**
     * Merges a few keys into an installed skill's config_values without touching
     * the rest. Unlike `modifyAgentSettings`, it never rewrites `enabled`/`ai_active`
     * and never trips the backend's config_schema check (skills whose marketplace
     * schema is empty but that keep their own keys, e.g. escalate_to_human).
     * No-op on the backend when the skill is not installed.
     * @param {string} skillId
     * @param {Object.<string, string>} configValues Keys to merge (values as strings)
     * @returns {Promise}
     */
    updateSkillConfigValues(skillId, configValues) {
        return apiRequest(
            'patch',
            `agent-settings/${store.state.user.user.account_id}/skills/${skillId}`,
            configValues,
        );
    },

    modifyAgentSettings(config_values, skillId, ai_active = undefined, enabled = true) {
        const updateData = {
            skill_id: skillId,
            agent_id: store.state.user.user.account_id,
            enabled: enabled,
            account_id: store.state.user.user.account_id,
            config_values: config_values,
        };
        if (ai_active !== undefined) {
            updateData.ai_active = ai_active;
        }

        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}/skills/${skillId}`, updateData);
    },

    enableAgentSettings() {
        const params = {
            account_id: store.state.user.user.account_id,
            agent_settings_id: store.state.user.user.account_id,
            enabled: true,
        };
        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}/enable`, params);
    },

    disableAgentSettings() {
        const params = {
            account_id: store.state.user.user.account_id,
            agent_settings_id: store.state.user.user.account_id,
            enabled: false,
        };
        return apiRequest('post', `agent-settings/${store.state.user.user.account_id}/disable`, params);
    },

    getWineobsWineryInfo(wineryId) {
        return apiRequest('get', `wineobs/wineries/${wineryId}`);
    },

    getWineobsTours() {
        return apiRequest('get', 'wineobs/tours');
    },

    /** @returns {Promise} List of active TurnoChic services */
    getTurnochicServices() {
        return apiRequest('get', 'turnochic/services');
    },

    /**
     * Modules, deal stages and fields of the connected Zoho organization. Comes back empty when the
     * token is missing the ZohoCRM.settings scopes.
     *
     * @returns {Promise}
     */
    getZohoCrmMetadata() {
        return apiRequest('get', 'zoho-crm/metadata');
    },

    /**
     * State of the Zoho notification channel, asked to Zoho itself: whether there is a live channel
     * and which callback URL it points to. Valid credentials do not mean the opportunities born in
     * Zoho arrive, and without this the difference is invisible.
     *
     * @returns {Promise}
     */
    getZohoCrmNotifications() {
        return apiRequest('get', 'zoho-crm/notifications');
    },

    /**
     * Validates the stored Zoho credentials against the API and autoconfigures module and stage.
     * The install event only fires the first time the skill is added, so this is what connects the
     * account when the credentials are saved (or fixed) afterwards.
     *
     * @returns {Promise}
     */
    connectZohoCrm() {
        return apiRequest('post', 'zoho-crm/connect');
    },

    /**
     * Re-derives the qualification assistant from the Zoho definition: schema, instructions and the
     * mapping of each answer to its Zoho field. Connecting only fills gaps, so this is what picks up
     * a pipeline or a custom field added afterwards. Overwrites manual edits of those keys.
     *
     * @returns {Promise}
     */
    resyncZohoCrmAssistant() {
        return apiRequest('post', 'zoho-crm/resync-assistant');
    },

    /**
     * WhatsApp templates the lead cycle can send: the approved ones of the account, each with the
     * names of its variables so the panel can warn about the ones the integration cannot fill.
     *
     * @returns {Promise}
     */
    getZohoCrmTemplates() {
        return apiRequest('get', 'zoho-crm/templates');
    },
};

export default agentSettingsService;
